import { Injectable } from '@angular/core';
import { COLUMN_DISPLAY_CONFIG, ColumnDisplay, ColumnDisplayEntry, NOWRAP_PATTERNS } from './column-display.config';
@Injectable({ providedIn: 'root' })
export class AutoCellTooltipService {
  private static readonly CELL_SELECTOR = 'th[class*="mat-column-"], td[class*="mat-column-"], td[data-auto-clamp]';

  private observer?: MutationObserver;

  private tooltipEl?: HTMLDivElement;
  private tooltipSurface?: HTMLDivElement;
  private hideTimer?: number;
  private currentCell: HTMLElement | null = null;
  private showTimer?: number;

  init(): void {
    if (typeof document === 'undefined') return;

    this.createTooltipElement();
    this.processAll(document.body);

    this.observer = new MutationObserver((mutations) => {
      const cells = new Set<HTMLElement>();
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          this.collectCells(node as HTMLElement, cells);
        }
        if (mutation.type === 'characterData') {
          const parent = mutation.target.parentElement;
          if (parent) {
            const cell = parent.closest(AutoCellTooltipService.CELL_SELECTOR) as HTMLElement | null;
            if (cell) cells.add(cell);
          }
        }
      }
      cells.forEach((cell) => this.processCell(cell));
    });

    this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    document.addEventListener('mouseover', this.onMouseOver, true);
    document.addEventListener('mouseout', this.onMouseOut, true);
    window.addEventListener('scroll', this.hideTooltip, true);
    window.addEventListener('resize', this.hideTooltip);
  }

  // ─── Tooltip element ──────────────────────────────────────────────────────

  private createTooltipElement(): void {
    const el = document.createElement('div');
    el.className = 'mdc-tooltip mat-mdc-tooltip';
    el.setAttribute('role', 'tooltip');
    el.style.cssText = [
      'position:fixed',
      'z-index:99999',
      'pointer-events:none',
      'opacity:0',
      'transform:translateY(6px)',
      'transition:opacity 200ms cubic-bezier(0,0,0.2,1),transform 200ms cubic-bezier(0,0,0.2,1)',
      'max-width:420px',
      'left:-9999px',
      'top:-9999px',
    ].join(';');

    const surface = document.createElement('div');
    surface.className = 'mdc-tooltip__surface mdc-tooltip__surface-animation mat-mdc-tooltip-surface';
    surface.style.cssText = ['transform:none !important', 'max-width:420px', 'word-break:break-word', 'border-radius:8px', 'padding:6px 10px'].join(';');

    el.appendChild(surface);
    document.body.appendChild(el);

    this.tooltipEl = el;
    this.tooltipSurface = surface;
  }

  private onMouseOver = (event: Event): void => {
    const target = (event as MouseEvent).target as HTMLElement | null;
    if (!target) return;
    const cell = target.closest('[data-auto-tooltip]') as HTMLElement | null;
    if (!cell || cell === this.currentCell) return;
    const text = cell.getAttribute('data-auto-tooltip');
    if (!text) return;
    this.currentCell = cell;
    clearTimeout(this.showTimer);
    this.showTimer = window.setTimeout(() => this.showTooltip(cell, text), 150);
  };

  private onMouseOut = (event: Event): void => {
    const cell = this.currentCell;
    if (!cell) return;
    const next = (event as MouseEvent).relatedTarget as Element | null;
    if (next && cell.contains(next)) return;
    this.currentCell = null;
    clearTimeout(this.showTimer);
    this.hideTooltip();
  };

  private showTooltip(cell: HTMLElement, text: string): void {
    const el = this.tooltipEl!;
    const surface = this.tooltipSurface!;

    surface.textContent = text;

    el.style.left = '-9999px';
    el.style.top = '-9999px';
    el.classList.add('mdc-tooltip--shown');

    requestAnimationFrame(() => {
      if (this.currentCell !== cell) return;
      const innerTarget = cell.querySelector('[data-clamp]') as HTMLElement | null;
      const anchor = innerTarget ?? cell;
      const anchorRect = anchor.getBoundingClientRect();
      const tipRect = el.getBoundingClientRect();

      let top = anchorRect.top - tipRect.height - 8;
      let left = anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;

      if (top < 8) top = anchorRect.bottom + 8;
      left = Math.max(8, Math.min(left, window.innerWidth - tipRect.width - 8));
      top = Math.max(8, Math.min(top, window.innerHeight - tipRect.height - 8));

      el.style.left = `${left}px`;
      el.style.top = `${top}px`;

      requestAnimationFrame(() => {
        if (this.currentCell !== cell) return;
        el.style.opacity = '1';
        el.style.transform = 'translateY(0)';
      });
    });
  }

  private hideTooltip = (): void => {
    clearTimeout(this.showTimer);
    clearTimeout(this.hideTimer);
    this.currentCell = null;

    if (!this.tooltipEl) return;

    this.tooltipEl.style.opacity = '0';
    this.tooltipEl.style.transform = 'translateY(6px)';
    this.tooltipEl.classList.remove('mdc-tooltip--shown');

    this.hideTimer = window.setTimeout(() => {
      if (this.tooltipEl) {
        this.tooltipEl.style.left = '-9999px';
        this.tooltipEl.style.top = '-9999px';
      }
    }, 160);
  };

  private processAll(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>(AutoCellTooltipService.CELL_SELECTOR).forEach((cell) => this.processCell(cell));
  }

  private collectCells(el: HTMLElement, target: Set<HTMLElement>): void {
    if (el.matches?.(AutoCellTooltipService.CELL_SELECTOR)) target.add(el);
    el.querySelectorAll?.<HTMLElement>(AutoCellTooltipService.CELL_SELECTOR).forEach((c) => target.add(c));
  }

  private processCell(cell: HTMLElement): void {
    if (cell.hasAttribute('data-no-auto-clamp')) return;

    const display = this.resolveDisplay(cell);
    if (!display) return;

    const entries: ColumnDisplayEntry[] = Array.isArray(display) ? display : [display];
    for (const entry of entries) {
      if (entry === 'nowrap') this.applyNowrap(cell);
      else if (entry === 'empty-only') this.applyEmptyOnly(cell);
      else this.applyClamp(cell, entry.clamp);
    }
  }

  private resolveDisplay(cell: HTMLElement): ColumnDisplay | null {
    const explicitClamp = cell.getAttribute('data-auto-clamp');
    if (explicitClamp) {
      const n = parseInt(explicitClamp, 10);
      if (!isNaN(n) && n >= 1 && n <= 3) return { clamp: n as 1 | 2 | 3 };
      return null;
    }

    const colClass = Array.from(cell.classList).find((c) => c.startsWith('mat-column-'));
    if (!colClass) return null;
    const colName = colClass.replace('mat-column-', '').replace(/_filter$/, '');

    const explicit = COLUMN_DISPLAY_CONFIG[colName];
    if (explicit) return explicit;
    if (NOWRAP_PATTERNS.some((p) => p.test(colName))) return 'nowrap';
    return null;
  }

  private applyEmptyOnly(cell: HTMLElement): void {
    if (cell.tagName !== 'TD') return;
    const rawText = (cell.textContent ?? '').trim();
    if (!rawText) {
      if (!cell.hasAttribute('data-empty')) cell.setAttribute('data-empty', '');
    } else {
      if (cell.hasAttribute('data-empty')) cell.removeAttribute('data-empty');
    }
  }

  private applyNowrap(cell: HTMLElement): void {
    if (!cell.hasAttribute('data-nowrap')) {
      cell.setAttribute('data-nowrap', '');
    }
    if (cell.hasAttribute('data-auto-tooltip')) {
      cell.removeAttribute('data-auto-tooltip');
    }
  }

  private applyClamp(cell: HTMLElement, lines: number): void {
    if (cell.tagName !== 'TD') return;

    // Empty-cell detection — show "Not Available" via CSS, skip clamp/tooltip
    const rawText = (cell.textContent ?? '').trim();
    if (!rawText) {
      if (!cell.hasAttribute('data-empty')) cell.setAttribute('data-empty', '');
      return;
    }
    if (cell.hasAttribute('data-empty')) cell.removeAttribute('data-empty');

    const target = !cell.firstElementChild ? this.wrapTextInSpan(cell) : this.findPrimaryTextElement(cell);
    if (!target) return;

    if (target.getAttribute('data-clamp') !== String(lines)) {
      target.setAttribute('data-clamp', String(lines));
    }

    if (cell.getAttribute('data-clamp-host') !== String(lines)) {
      cell.setAttribute('data-clamp-host', String(lines));
    }

    this.scheduleOverflowCheck(() => this.updateClampTooltip(cell, target));
  }

  private scheduleOverflowCheck(check: () => void): void {
    requestAnimationFrame(check);
    setTimeout(check, 150);
  }

  private wrapTextInSpan(cell: HTMLElement): HTMLElement | null {
    const text = (cell.textContent ?? '').trim();
    if (!text) return null;
    cell.textContent = '';
    const span = document.createElement('span');
    span.textContent = text;
    cell.appendChild(span);
    return span;
  }

  private findPrimaryTextElement(cell: HTMLElement): HTMLElement | null {
    for (const child of Array.from(cell.children) as HTMLElement[]) {
      const text = (child.textContent ?? '').trim();
      if (text && child.children.length === 0) {
        return child;
      }
    }
    const walk = (el: HTMLElement): HTMLElement | null => {
      for (const child of Array.from(el.children) as HTMLElement[]) {
        const text = (child.textContent ?? '').trim();
        if (text && child.children.length === 0) return child;
        const found = walk(child);
        if (found) return found;
      }
      return null;
    };
    return walk(cell);
  }

  private updateClampTooltip(cell: HTMLElement, target: HTMLElement): void {
    const isOverflowing = target.scrollHeight > target.clientHeight + 1;
    if (isOverflowing) {
      const text = (target.textContent ?? '').trim();
      if (text && cell.getAttribute('data-auto-tooltip') !== text) {
        cell.setAttribute('data-auto-tooltip', text);
      }
    } else if (cell.hasAttribute('data-auto-tooltip')) {
      cell.removeAttribute('data-auto-tooltip');
    }
    if (cell.hasAttribute('title')) cell.removeAttribute('title');
  }
}
